import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

// components
import Button from "@surgia/ui/components/button";
import GenericModal from "@surgia/ui/components/genericModal";
import { PurchaseOrderTableUntitled } from "../../../containers/tables/PurchaseOrderTableUntitled";

// queries
import { branchQueries } from "@surgia/api/src/queryStore/shared/branch";
import { sharedQueries } from "@surgia/api/src/queryStore/shared"; // Add shared queries import
import config from "@surgia/config";

// icons

// constants
const ITEMS_PER_PAGE = 8;

// Add import for routes
import { ADMIN_PROTECTED_ROUTES } from "../../../routes";

// Filters Modal Component
export const FiltersModal = ({
  open,
  onClose,
  onApply,
  filters,
  setFilters,
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  filters: any;
  setFilters: (f: any) => void;
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = React.useState(filters);

  // Keep localFilters in sync with parent filters when modal opens
  React.useEffect(() => {
    if (open) setLocalFilters(filters);
  }, [open, filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({
      ...localFilters,
      id: "",
      branch_id: "",
    });
  };

  return (
    <GenericModal
      title={t("filters")}
      open={open}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleClear}
            className="text-[#0033CC] font-medium"
          >
            {t("admin.clearFilters")}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t("filter")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* ID Filter */}
        <div>
          <label className="block text-[14px] mb-1">
            {t("shared.table.id")}
          </label>
          <div className="relative">
            <input
              type="text"
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300"
              placeholder={t("admin.idRange")}
              value={localFilters.id || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[\d-]*$/.test(value) || value === "") {
                  setLocalFilters({ ...localFilters, id: value });
                }
              }}
              inputMode="numeric"
              pattern="[\d-]*"
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

function PurchaseOrder() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { id: tenantId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [status, setStatus] = useState<string | undefined>("SUBMITTED");
  const [search] = useState("");
  const [filters, setFilters] = useState({
    id: "",
    branch_id: "",
  });
  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState<
    string[]
  >([]);

  // Add branches state
  const [selectedBranch, setSelectedBranch] = useState<string | null>();
  const [allUserBranches, setAllUserBranches] = useState<string>("");

  // Add sort tracking state
  const [currentSort, setCurrentSort] = useState<{
    column: string | null;
    direction: string | null;
  }>({ column: null, direction: null });

  // Fetch branches for the dropdown
  const { data: branchesResponse, isLoading: isBranchesLoading } = useQuery({
    ...branchQueries.queries.getBranchesForDropdown({
      tenant_id: parseInt(tenantId!),
      component_id: config.componentId,
    }),
  });

  // Get branches from the API response
  const branches = branchesResponse?.data || [];

  // Set allUserBranches when branches are loaded
  useEffect(() => {
    if (branches.length > 0) {
      const branchIds = branches.map((branch: any) => branch.id).join(",");
      setAllUserBranches(branchIds);
    }
  }, [branches]);

  // Track sort changes
  useEffect(() => {
    const sortParam = searchParams.get("sort");
    if (sortParam) {
      const [column, direction] = sortParam.split(":");
      const newSort = { column, direction };

      // Only log if the sort has actually changed
      if (
        currentSort.column !== column ||
        currentSort.direction !== direction
      ) {
        setCurrentSort(newSort);
        setCurrentPage(0);
      }
    }
  }, [searchParams]);
  // Fetch purchase orders data using real API
  const { data: purchaseOrdersResponse, isLoading } = useQuery({
    ...sharedQueries.purchaseOrder.queries.getPurchaseOrdersList({
      componentId: String(config.componentId),
      tenantId: tenantId || "",
      branchesIds:
        selectedBranch || branches?.map((item: any) => item?.id)?.join(","),
      sort: (() => {
        const sortParam = searchParams.get("sort");
        if (sortParam) {
          const [column, direction] = sortParam.split(":");
          // Map "ascending" to "asc" and "descending" to "desc"
          const mappedDirection = direction === "ascending" ? "asc" : "desc";
          return `${column},${mappedDirection}`;
        }
        // Default sort: id descending
        return "id,desc";
      })(),
      page: currentPage,
      size: ITEMS_PER_PAGE,
    }),
    enabled: !!(config.componentId && tenantId),
    select: (data) => {
      if ("errorCode" in data) {
        return {
          data: [],
          headers: {},
        };
      }
      if (Array.isArray(data.data)) {
        const transformed = data.data.map((po: any) => ({
          id: po.id,
          orderedBy: po.ordered_by,
          branch: po.branch?.name || "N/A",
          totalQuantity: po.total_quantity,
          "orderSummary.total": po.order_summary?.total || 0,
          createdAt: po.created_at,
          status: po.status,
          branchId: po.branch?.id || null,
          numberOfPurchaseRequests: po.number_of_purchase_requests,
        }));
        return {
          data: transformed,
          headers: data.headers,
        };
      }
      return {
        data: [],
        headers: {},
      };
    },
  });

  useEffect(() => {
    setCurrentPage(0);
  }, [search, status, filters.id, selectedBranch]); // Reset page when branch changes

  const handleViewPODetails = (poId: string) => {
    // Navigate to the Purchase Order Details View page
    navigate(
      ADMIN_PROTECTED_ROUTES.TENANT_DETAILS_PURCHASE_ORDER_DETAILS.replace(
        ":id",
        tenantId || ""
      ).replace(":poId", poId)
    );
  };

  // Use real data instead of mock data
  const transformedData = purchaseOrdersResponse?.data || [];

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="bg-white">
      {/* Purchase Orders Table Section using Untitled UI */}
      <PurchaseOrderTableUntitled
        data={transformedData}
        loading={isLoading}
        currentPage={currentPage}
        totalPages={Math.ceil(
          (purchaseOrdersResponse?.headers?.["x-total-count"] || 0) /
            ITEMS_PER_PAGE
        )}
        onPageChange={setCurrentPage}
        showBranchColumn={true}
        selectedPurchaseOrders={selectedPurchaseOrders}
        setSelectedPurchaseOrders={setSelectedPurchaseOrders}
        status={status}
        setStatus={setStatus}
        onViewDetails={handleViewPODetails}
        // Add branches dropdown props
        showBranchesDropDown={true}
        branches={branches}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        allUserBranches={allUserBranches}
        showAllBranches={true}
        isBranchesLoading={isBranchesLoading}
        totalCount={purchaseOrdersResponse?.headers?.["x-total-count"] || 0}
      />

      {/* Filters Modal */}
      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}
    </div>
  );
}

export default PurchaseOrder;
